import React, { useMemo } from "react";
import { Stack, Table, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import {
  DATA_AR_COMMISSION_ACCOUNT_TYPE,
  DATA_AR_SUBMISSION_VIA,
} from "../data/options";
import { getArFormValues } from "../helpers/arFormUtils";
import { buildCommissionChargeSummary } from "../helpers/investmentTransactionCommissionUtils";
import OrderArFormBlock, { OrderArFormStack } from "./orderArFormBlock";

const formatPercent = (value) =>
  value == null ? "—" : `${Number(value).toFixed(2)}%`;

const resolveLabel = (options, value) =>
  options.find((option) => option.value === value)?.label ?? value ?? "—";

const OrderInvestmentTransactionCommission = ({ form }) => {
  const { t } = useTranslation();
  const arForm = getArFormValues(form);
  const commission = arForm.commission ?? {};
  const submissionVia = arForm.submissionVia || "Online";
  const isWrap = commission.accountType === "Wrap";

  const chargeSummary = useMemo(
    () => buildCommissionChargeSummary({ submissionVia, commission }),
    [submissionVia, commission]
  );

  if (!chargeSummary) {
    return (
      <OrderArFormStack>
        <OrderArFormBlock
          title={t("iFast Charge Breakdown")}
          subtitle="iFast Master Logsheet — charge split from client upfront fee rate"
        >
          <Text size="sm" color="dimmed">
            Complete Part C on the Transaction Detail tab (account type and fee
            rate) to see the commission breakdown.
          </Text>
        </OrderArFormBlock>
      </OrderArFormStack>
    );
  }

  return (
    <OrderArFormStack>
      <OrderArFormBlock
        title={t("iFast Charge Breakdown")}
        subtitle="iFast Master Logsheet — charge split from client upfront fee rate"
      >
        <Stack spacing="sm">
          <Text size="sm" color="dimmed">
            {t("Submission via")}: {resolveLabel(DATA_AR_SUBMISSION_VIA, submissionVia)}
          </Text>
          <Text size="sm" color="dimmed">
            Account type 賬戶類別:{" "}
            {resolveLabel(DATA_AR_COMMISSION_ACCOUNT_TYPE, commission.accountType)}
          </Text>
          <Table withBorder withColumnBorders fontSize="sm">
            <thead>
              <tr>
                <th>{t("Total Charge")}</th>
                <th>{t("iFast Charge")}</th>
                <th>{t("FP Charge")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{formatPercent(chargeSummary.totalCharge)}</td>
                <td>{formatPercent(chargeSummary.ifastCharge)}</td>
                <td>{formatPercent(chargeSummary.fpCharge)}</td>
              </tr>
            </tbody>
          </Table>
          <Text size="xs" color="dimmed">
            {t("FP Charge")} = {t("Total Charge")} − {t("iFast Charge")}.{" "}
            {isWrap
              ? "Wrap: iFast = upfront fee × 6% (Online) or × 10% (Paper); upfront wrap/switch fee optional 0%–5%."
              : "Non-Wrap: iFast fixed at 0.3% (Online) or 0.5% (Paper); subscription fee 1%–5%, switching fee 1%–3%."}
          </Text>
        </Stack>
      </OrderArFormBlock>
    </OrderArFormStack>
  );
};

export default OrderInvestmentTransactionCommission;
